import { useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { useClient } from '../../../hooks/useClient';
import { useForm } from '../../../hooks/useForm';
import { Reservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { Organization } from '../../../types/organization';
import { ExternalUserShort } from '../../../types/externalUser';
import { CancellationForm, getCancellationFormValidators, getInitialCancellationForm, ReservationForm } from '../validators';
import { CancellationInfo, ExternalUserWithCancellation } from '../types';
import { getCancellationPayload } from '../utils/form';
import { mutateUserProfileData } from '../utils/cache';

/** An SWR `mutate` called with no data: it clears or revalidates the cached value. */
type Revalidate = (data?: undefined) => Promise<unknown>;

interface Options {
  resource: Reservation | undefined;
  visible: boolean;
  reservationType: ReservationType | undefined;
  isGroupReservation: boolean;
  isUnregisteredClient: boolean;
  form: ReservationForm;
  setFormFields: (fields: DeepPartial<ReservationForm>) => void;
  organization: Organization | undefined;
  externalUserProfileId: string | undefined;
  mutateReservations: KeyedMutator<Reservation[] | undefined>;
  mutateOrgCoupons: Revalidate;
  mutateActiveUserCoupons: Revalidate;
  mutateSelectedUserCoupon: Revalidate;
  /** Closes the panel. */
  onHide: () => void;
}

/**
 * Cancels the whole reservation, or one client of a group reservation. A group client is only marked as canceled in
 * `canceledUsers`; the cancellation reaches the backend when the reservation is saved.
 */
export const useReservationCancellation = ({
  resource,
  visible,
  reservationType,
  isGroupReservation,
  isUnregisteredClient,
  form,
  setFormFields,
  organization,
  externalUserProfileId,
  mutateReservations,
  mutateOrgCoupons,
  mutateActiveUserCoupons,
  mutateSelectedUserCoupon,
  onHide,
}: Options) => {
  const { post, delete: deleteResource } = useClient();
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [showGroupCancellationDialog, setShowGroupCancellationDialog] = useState(false);
  const [userToCancel, setUserToCancel] = useState<ExternalUserShort>();
  const [canceledUsers, setCanceledUsers] = useState<ExternalUserWithCancellation[]>([]);

  const isCancelingGroupWithMultipleUsers =
    !!resource?.externalUsers?.length && resource?.externalUsers?.length > 1 && showGroupCancellationDialog;
  const initialCancellationForm = useMemo(
    () => getInitialCancellationForm(isCancelingGroupWithMultipleUsers),
    [isCancelingGroupWithMultipleUsers],
  );
  const cancellationFormValidators = useMemo(
    () => getCancellationFormValidators(isCancelingGroupWithMultipleUsers),
    [isCancelingGroupWithMultipleUsers],
  );
  const {
    form: cancellationForm,
    setFormFields: setCancellationFormFields,
    isSaving: isCanceling,
    setIsSaving: setIsCanceling,
    validationErrors: cancellationErrors,
    resetForm: resetCancellationForm,
    validate: validateCancellationForm,
  } = useForm<CancellationForm>(initialCancellationForm, !visible, cancellationFormValidators);

  const handleUserCancellation = (user: ExternalUserShort) => {
    resetCancellationForm();
    setUserToCancel(user);
    setShowCancellationDialog(true);
  };

  const handleDelete = async (): Promise<boolean> => {
    if (!resource) return false;

    if (isUnregisteredClient) {
      setIsCanceling(true);

      const response = await deleteResource(`/reservations/${resource.id}`, {
        successMessage: {
          summary: 'Reserva eliminada',
          detail: 'La reserva se ha eliminado correctamente',
        },
        errorMessages: {
          summary: 'Error al cancelar la reserva',
          defaultDetail: 'No se ha podido cancelar la reserva',
        },
      });

      if (response) {
        mutateReservations();
        mutateActiveUserCoupons();
        mutateSelectedUserCoupon(undefined);
        if (externalUserProfileId && organization) {
          mutateUserProfileData(organization, externalUserProfileId);
        }
        onHide();
      }

      setIsCanceling(false);
      return !!response;
    }

    resetCancellationForm();
    setShowGroupCancellationDialog(true);
    return false;
  };

  const cancelReservation = async (cancellation: CancellationInfo) => {
    const response = await post(`/reservations/${resource!.id}/cancel`, {
      body: { cancellation },
      successMessage: {
        summary: 'Reserva cancelada',
        detail: 'La reserva se ha cancelado correctamente',
      },
      errorMessages: {
        summary: 'Error al cancelar la reserva',
        defaultDetail: 'No se ha podido cancelar la reserva',
      },
    });

    if (response) {
      mutateReservations();
      mutateOrgCoupons();
      mutateActiveUserCoupons();
      mutateSelectedUserCoupon(undefined);
      if (externalUserProfileId && organization) {
        mutateUserProfileData(organization, externalUserProfileId);
      }
      onHide();
    }
  };

  const handleConfirmCancellation = async () => {
    const errors = validateCancellationForm();
    if (errors) return;

    setIsCanceling(true);
    if (showGroupCancellationDialog) {
      // Cancelar toda la reserva
      await cancelReservation(getCancellationPayload(cancellationForm, isCancelingGroupWithMultipleUsers));
      setShowGroupCancellationDialog(false);
    } else {
      // Para reservas de grupo, guardamos la información de cancelación
      if (isGroupReservation && userToCancel) {
        const cancellation = getCancellationPayload(cancellationForm);
        // Actualizar la lista de usuarios confirmados y mover el siguiente de la cola si existe
        const updatedConfirmedUsers = form?.confirmedExternalUsers?.filter((u) => u.id !== userToCancel.id) ?? [];

        // Si hay usuarios en cola y no se ha llegado al límite de usuarios confirmados, mover el primero a confirmados
        let updatedQueuedUsers = [...(form?.queuedExternalUsers ?? [])];
        if (updatedQueuedUsers.length > 0 && updatedConfirmedUsers.length < (reservationType?.groupSize ?? 0)) {
          const [nextUser, ...remainingQueuedUsers] = updatedQueuedUsers;
          updatedConfirmedUsers.push(nextUser);
          updatedQueuedUsers = remainingQueuedUsers;
        }

        // Guardar el usuario cancelado con su información de cancelación
        setCanceledUsers([...canceledUsers, { ...userToCancel, cancellation }]);

        setFormFields({
          confirmedExternalUsers: updatedConfirmedUsers,
          queuedExternalUsers: updatedQueuedUsers,
        });
      } else {
        // Para reservas individuales, usar el mismo endpoint que para cancelación completa
        await cancelReservation(getCancellationPayload(cancellationForm));
      }

      setShowCancellationDialog(false);
    }
    setIsCanceling(false);

    setUserToCancel(undefined);
  };

  return {
    showCancellationDialog,
    setShowCancellationDialog,
    showGroupCancellationDialog,
    setShowGroupCancellationDialog,
    userToCancel,
    setUserToCancel,
    canceledUsers,
    setCanceledUsers,
    isCancelingGroupWithMultipleUsers,
    cancellationForm,
    setCancellationFormFields,
    cancellationErrors,
    isCanceling,
    handleUserCancellation,
    handleDelete,
    handleConfirmCancellation,
  };
};
